import { getContext, call, put, select, fork, take } from 'redux-saga/effects';
import { SupabaseClient } from '@supabase/supabase-js';
import { PapiDamz, PapiDamzSession } from '@wix/da-papi-types';
import { isApiError, requestPuppy } from '@wix/da-http-client';
import { damzOffline, damzOnline } from '../actions/initialization';
import { initUserSettings } from '../actions/settings';
import { getIsVerificationNeeded } from '@wix/da-shared-react/pkg/publicSession/selectors';
import { eventChannel } from 'redux-saga';
import { realtimeConnections } from '../RealtimeConnections';
import { sessionLost } from '../actions/auth';
import ddt from '@wix/da-ddt';

function* initOrFetchSession(client: SupabaseClient) {
  // are they already loggedin?
  const userRes = yield client.auth.getUser();

  if (!userRes.error) {
    yield put(damzOnline());
    return;
  }

  // No auth, try and fetch it
  const result: PapiDamzSession = yield call(
    requestPuppy,
    {
      method: 'get',
      url: `/session`,
    },
    undefined,
    'damz'
  );

  if (!result || isApiError(result) || !result.supaSession) {
    yield put(damzOffline('Failed to connect to message network'));
    return;
  }

  try {
    const session = JSON.parse(result.supaSession);
    const authRes = yield client.auth.setSession(session);
    if (authRes.error) {
      yield put(damzOffline('Failed to connect to message network'));
      return;
    }
    yield put(damzOnline());
  } catch (e) {
    yield put(damzOffline('Failed to connect to message network'));
  }
}

/**
 * Responsible for setting up the supabase session, SSR will create the
 * session once and then the client should be able to refresh forever after
 * that, the fallback is the to call the sesson endpoint if refresh et fails.
 */
export function* authSaga() {
  const client: SupabaseClient = yield getContext('supaClient');
  const damzConfig: PapiDamz = yield getContext('damzConfig');
  const isVerificationNeeded = yield select(getIsVerificationNeeded);

  if (isVerificationNeeded) {
    yield put(damzOffline('Verification needed'));
    return;
  }

  // Sync the settings
  yield put(
    initUserSettings({
      disableSounds: damzConfig.settings?.disableSounds ?? false,
      onlyWatch: damzConfig.settings?.onlyWatch ?? false,
      status: 'uninitialized',
    })
  );

  // We have auth from server SSR, just set the session
  if (damzConfig.authSession) {
    try {
      const session = JSON.parse(damzConfig.authSession);
      const authRes = yield client.auth.setSession(session);
      if (authRes.error) {
        yield call(initOrFetchSession, client);
        return;
      }
      yield put(damzOnline());
    } catch (e) {
      yield call(initOrFetchSession, client);
    }
  } else {
    yield call(initOrFetchSession, client);
  }
}

/**
 * Saga to handle session loss and automatic recovery with channel reconnection
 */
export function* sessionLostSaga() {
  const client: SupabaseClient = yield getContext('supaClient');

  // Set up callback to handle session loss
  const sessionLostChannel = eventChannel(emitter => {
    realtimeConnections.setSessionLostCallback(reconnect => {
      emitter(sessionLost(reconnect));
    });
    return () => {
      realtimeConnections.setSessionLostCallback(null);
    };
  });

  // Fork a saga to listen for session lost events
  yield fork(function* () {
    while (true) {
      const action: ReturnType<typeof sessionLost> =
        yield take(sessionLostChannel);
      ddt.log('damz.auth', 'Session lost detected, fetching new session');

      try {
        // Fetch new session
        yield call(initOrFetchSession, client);

        // After successful session fetch, reconnect channels
        ddt.log('damz.auth', 'New session fetched, reconnecting channels');
        yield call(action.payload.reconnect);
      } catch (error) {
        ddt.error('damz.auth', 'Failed to recover from session loss', error);
      }
    }
  });
}
